import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, setDoc, deleteField } from "firebase/firestore";
import {
  Star,
  Trophy,
  Plus,
  Lock,
  LockOpen,
  Trash2,
  Users,
  Check,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ColorPicker, PALETTE } from "../components/color-picker";
import { db, isFirebaseConfigured } from "../lib/firebase";

// All tracker data lives in a single Firestore document, synced live.
const TRACKER_ID = "board-games";
const IDENTITY_KEY = "boardGameTrackerIdentity"; // localStorage: my memberId
const ADMIN_KEY = "boardGameTrackerAdmin"; // localStorage: "1" when unlocked
// Simple shared password gating "add game" + "record winner". This is a soft,
// client-side gate only (see the security note in the project plan) — fine for
// a hobby group, not real auth.
const ADMIN_PASSWORD = "keyoniiscool";
// Guests are lightweight members (no claimed color yet) shown in a neutral gray.
const GUEST_COLOR = "#9ca3af";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Local "YYYY-MM-DD" key (avoids UTC off-by-one from toISOString).
const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Format a "YYYY-MM-DD" key for display, e.g. "Sat, Jun 14, 2026".
const formatDate = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("default", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Members credited with a win for a play. Supports the legacy single
// `winnerId` field and the newer `winnerIds` array (teams + co-op wins, where
// everyone who played is credited).
const winnersOf = (play) =>
  play.winnerIds || (play.winnerId ? [play.winnerId] : []);

const inputClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
const numInputClass =
  "h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";

// Five-star widget. Read-only when `onChange` is omitted.
const Stars = ({ value = 0, onChange }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= value;
      const star = (
        <Star
          className={`h-4 w-4 ${
            filled ? "fill-amber-400 text-amber-400" : "text-muted-foreground"
          }`}
        />
      );
      if (!onChange) return <span key={n}>{star}</span>;
      return (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n === value ? 0 : n)}
          className="transition-transform hover:scale-110"
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          {star}
        </button>
      );
    })}
  </span>
);

// Editor for the current member's own rating + comment on one game. Keeps the
// in-progress text local so live snapshots don't clobber what you're typing.
const MyRatingEditor = ({ existing, onSave }) => {
  const [stars, setStars] = useState(existing?.stars || 0);
  const [comment, setComment] = useState(existing?.comment || "");

  // Re-sync from props only when the saved value actually changes.
  useEffect(() => {
    setStars(existing?.stars || 0);
    setComment(existing?.comment || "");
  }, [existing?.stars, existing?.comment]);

  const dirty =
    stars !== (existing?.stars || 0) || comment !== (existing?.comment || "");

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="flex items-center gap-2">
        <Label className="text-xs">Your rating</Label>
        <Stars value={stars} onChange={setStars} />
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Leave a comment…"
        rows={2}
        className={`${inputClass} h-auto min-h-[3rem] resize-y`}
      />
      <Button
        size="sm"
        disabled={!dirty}
        onClick={() => onSave({ stars, comment: comment.trim() })}
      >
        Save my rating
      </Button>
    </div>
  );
};

const BoardGameTracker = () => {
  const [data, setData] = useState({ members: {}, games: {}, plays: {} });
  const [loaded, setLoaded] = useState(false);
  const [myId, setMyId] = useState(
    () => localStorage.getItem(IDENTITY_KEY) || null
  );
  const [isAdmin, setIsAdmin] = useState(
    () => localStorage.getItem(ADMIN_KEY) === "1"
  );

  // Onboarding form
  const [nameInput, setNameInput] = useState("");
  const [colorInput, setColorInput] = useState("");

  // Admin unlock
  const [showAdminInput, setShowAdminInput] = useState(false);
  const [adminInput, setAdminInput] = useState("");
  const [adminError, setAdminError] = useState(false);

  // Admin forms
  const [gameNameInput, setGameNameInput] = useState("");
  const [playGameId, setPlayGameId] = useState("");
  const [playDate, setPlayDate] = useState(() => dateKey(new Date()));
  const [playMode, setPlayMode] = useState("competitive"); // or "coop"
  const [playWinnerIds, setPlayWinnerIds] = useState([]); // competitive winner(s)
  const [playCoopResult, setPlayCoopResult] = useState("won"); // "won" | "lost"
  const [playPlayers, setPlayPlayers] = useState([]); // co-op participants
  // Optional per-player place/score rows: [{ playerId, place, score }]
  const [playResults, setPlayResults] = useState([]);
  const [guestNameInput, setGuestNameInput] = useState("");

  const ref = useMemo(
    () => (db ? doc(db, "trackers", TRACKER_ID) : null),
    []
  );

  useEffect(() => {
    if (!ref) return undefined;
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setData({
        members: d.members || {},
        games: d.games || {},
        plays: d.plays || {},
      });
      setLoaded(true);
    });
    return unsub;
  }, [ref]);

  const members = useMemo(() => data.members || {}, [data.members]);
  const games = useMemo(() => data.games || {}, [data.games]);
  const plays = useMemo(() => data.plays || {}, [data.plays]);
  const me = myId ? members[myId] : null;
  const usedColors = Object.values(members)
    .map((m) => m.color)
    .filter(Boolean);

  // ---- Writes -----------------------------------------------------------

  const claimMember = async () => {
    const name = nameInput.trim();
    if (!name || !colorInput) return;
    const id = newId();
    await setDoc(
      ref,
      { members: { [id]: { name, color: colorInput } } },
      { merge: true }
    );
    localStorage.setItem(IDENTITY_KEY, id);
    setMyId(id);
  };

  const adoptIdentity = (id) => {
    localStorage.setItem(IDENTITY_KEY, id);
    setMyId(id);
  };

  // Claim an existing entry as "me" on this device. Claiming a guest also
  // clears the guest flag (they're a real person now); they can pick a color.
  const claimMemberIdentity = async (id) => {
    if (members[id]?.guest) {
      await setDoc(
        ref,
        { members: { [id]: { guest: deleteField() } } },
        { merge: true }
      );
    }
    adoptIdentity(id);
  };

  const switchIdentity = () => {
    localStorage.removeItem(IDENTITY_KEY);
    setMyId(null);
    setNameInput("");
    setColorInput("");
  };

  const changeMyColor = async (color) => {
    if (!me) return;
    await setDoc(
      ref,
      { members: { [myId]: { ...me, color } } },
      { merge: true }
    );
  };

  const unlockAdmin = () => {
    if (adminInput === ADMIN_PASSWORD) {
      localStorage.setItem(ADMIN_KEY, "1");
      setIsAdmin(true);
      setShowAdminInput(false);
      setAdminInput("");
      setAdminError(false);
    } else {
      setAdminError(true);
    }
  };

  const lockAdmin = () => {
    localStorage.removeItem(ADMIN_KEY);
    setIsAdmin(false);
  };

  const addGame = async () => {
    const name = gameNameInput.trim();
    if (!name || !isAdmin) return;
    const id = newId();
    await setDoc(
      ref,
      { games: { [id]: { name, addedAt: Date.now() } } },
      { merge: true }
    );
    setGameNameInput("");
  };

  const deleteGame = async (gameId) => {
    if (!isAdmin) return;
    // Remove the game and any plays that reference it in one write.
    const playUpdates = {};
    Object.entries(plays).forEach(([pid, p]) => {
      if (p.gameId === gameId) playUpdates[pid] = deleteField();
    });
    await setDoc(
      ref,
      { games: { [gameId]: deleteField() }, plays: playUpdates },
      { merge: true }
    );
  };

  // Add a one-off guest as a lightweight (claimable) member. Returns the id.
  const addGuest = async () => {
    const name = guestNameInput.trim();
    if (!name || !isAdmin) return null;
    const id = newId();
    await setDoc(
      ref,
      { members: { [id]: { name, color: GUEST_COLOR, guest: true } } },
      { merge: true }
    );
    setGuestNameInput("");
    // Convenience: add the new guest to whichever list is currently active.
    if (playMode === "coop") setPlayPlayers((ids) => [...ids, id]);
    else setPlayWinnerIds((ids) => [...ids, id]);
    return id;
  };

  // Toggle a member in the active winner/player selection.
  const togglePlayer = (id) => {
    const setter = playMode === "coop" ? setPlayPlayers : setPlayWinnerIds;
    setter((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    );
  };

  // Promote a guest to a full member (admin): clear the flag, give a free color.
  const promoteGuest = async (memberId) => {
    if (!isAdmin) return;
    const freeColor =
      PALETTE.map((c) => c.value).find((v) => !usedColors.includes(v)) ||
      GUEST_COLOR;
    await setDoc(
      ref,
      { members: { [memberId]: { guest: deleteField(), color: freeColor } } },
      { merge: true }
    );
  };

  // Delete a member (admin) and cascade: remove their ratings, the plays they
  // won, and their result entries in other plays — all in one write.
  const deleteMember = async (memberId) => {
    if (!isAdmin) return;
    const gameUpdates = {};
    Object.entries(games).forEach(([gid, g]) => {
      if (g.ratings && g.ratings[memberId]) {
        gameUpdates[gid] = { ratings: { [memberId]: deleteField() } };
      }
    });
    const playUpdates = {};
    Object.entries(plays).forEach(([pid, p]) => {
      const winners = winnersOf(p);
      const players = p.players || [];
      const inResults = Boolean(p.results && p.results[memberId]);
      if (!winners.includes(memberId) && !players.includes(memberId) && !inResults)
        return;
      const newWinners = winners.filter((w) => w !== memberId);
      const newPlayers = players.filter((w) => w !== memberId);
      // Nothing meaningful left on this play — drop it entirely.
      if (newWinners.length === 0 && newPlayers.length === 0) {
        playUpdates[pid] = deleteField();
        return;
      }
      // Otherwise just remove them; normalize the legacy winnerId field too.
      const patch = { winnerIds: newWinners, winnerId: deleteField() };
      if (p.players) patch.players = newPlayers;
      if (inResults) patch.results = { [memberId]: deleteField() };
      playUpdates[pid] = patch;
    });
    await setDoc(
      ref,
      {
        members: { [memberId]: deleteField() },
        games: gameUpdates,
        plays: playUpdates,
      },
      { merge: true }
    );
    if (memberId === myId) switchIdentity();
  };

  const saveMyRating = async (gameId, { stars, comment }) => {
    if (!myId) return;
    await setDoc(
      ref,
      {
        games: {
          [gameId]: {
            ratings: {
              [myId]: { stars, comment, updatedAt: Date.now() },
            },
          },
        },
      },
      { merge: true }
    );
  };

  // Optional per-player place/score row helpers.
  const addResultRow = () =>
    setPlayResults((rows) => [...rows, { playerId: "", place: "", score: "" }]);
  const updateResultRow = (i, field, value) =>
    setPlayResults((rows) =>
      rows.map((row, idx) => (idx === i ? { ...row, [field]: value } : row))
    );
  const removeResultRow = (i) =>
    setPlayResults((rows) => rows.filter((_, idx) => idx !== i));

  const addPlay = async () => {
    if (!isAdmin || !playGameId || !playDate) return;
    const id = newId();
    const play = {
      gameId: playGameId,
      date: playDate,
      mode: playMode,
      addedAt: Date.now(),
    };
    if (playMode === "coop") {
      if (playPlayers.length === 0) return;
      play.coopResult = playCoopResult;
      play.players = playPlayers;
      // A co-op win credits everyone who played; a loss credits no one.
      play.winnerIds = playCoopResult === "won" ? playPlayers : [];
    } else {
      if (playWinnerIds.length === 0) return;
      play.winnerIds = playWinnerIds; // one = solo, many = team/tie
    }
    // Build the optional results map; keep only rows with a player and a value.
    const results = {};
    playResults.forEach(({ playerId, place, score }) => {
      if (!playerId) return;
      const entry = {};
      if (place !== "" && place != null) entry.place = Number(place);
      if (score !== "" && score != null) entry.score = Number(score);
      if (Object.keys(entry).length) results[playerId] = entry;
    });
    if (Object.keys(results).length) play.results = results;
    await setDoc(ref, { plays: { [id]: play } }, { merge: true });
    setPlayGameId("");
    setPlayMode("competitive");
    setPlayWinnerIds([]);
    setPlayCoopResult("won");
    setPlayPlayers([]);
    setPlayResults([]);
    setPlayDate(dateKey(new Date()));
  };

  const deletePlay = async (playId) => {
    if (!isAdmin) return;
    await setDoc(ref, { plays: { [playId]: deleteField() } }, { merge: true });
  };

  // ---- Derived data -----------------------------------------------------

  const memberEntries = useMemo(
    () =>
      Object.entries(members).sort((a, b) =>
        (a[1].name || "").localeCompare(b[1].name || "")
      ),
    [members]
  );

  const gameEntries = useMemo(
    () =>
      Object.entries(games).sort((a, b) =>
        (a[1].name || "").localeCompare(b[1].name || "")
      ),
    [games]
  );

  // Wins per member, ranked.
  const leaderboard = useMemo(() => {
    const wins = {};
    Object.values(plays).forEach((p) => {
      winnersOf(p).forEach((wid) => {
        wins[wid] = (wins[wid] || 0) + 1;
      });
    });
    return Object.entries(members)
      .map(([id, m]) => ({ id, ...m, wins: wins[id] || 0 }))
      .sort((a, b) => b.wins - a.wins || a.name.localeCompare(b.name));
  }, [members, plays]);

  const playsByGame = useMemo(() => {
    const grouped = {};
    Object.entries(plays).forEach(([pid, p]) => {
      (grouped[p.gameId] = grouped[p.gameId] || []).push({ pid, ...p });
    });
    Object.values(grouped).forEach((list) =>
      list.sort((a, b) => (a.date < b.date ? 1 : -1))
    );
    return grouped;
  }, [plays]);

  // ---- Render guards ----------------------------------------------------

  if (!isFirebaseConfigured) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl">Board Game Tracker</CardTitle>
              <CardDescription>Not configured yet</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                This page needs Firebase to sync between devices. Add the{" "}
                <code>REACT_APP_FIREBASE_*</code> values (see{" "}
                <code>.env.example</code>) to your local <code>.env</code> and to
                the Vercel project's Environment Variables, then redeploy.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-2xl text-center text-muted-foreground">
          Loading tracker…
        </div>
      </div>
    );
  }

  // ---- Onboarding (no identity on this device yet) ----------------------

  if (!myId) {
    const canPick = nameInput.trim() && colorInput;
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-2xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl">Board Game Tracker</CardTitle>
              <CardDescription>
                Add yourself so you can rate games and leave comments.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {memberEntries.length > 0 && (
                <div className="space-y-2">
                  <Label>Already added (or a guest)? That's me:</Label>
                  <div className="flex flex-wrap gap-2">
                    {memberEntries.map(([id, m]) => (
                      <Button
                        key={id}
                        variant="outline"
                        onClick={() => claimMemberIdentity(id)}
                        className="gap-2"
                      >
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: m.color }}
                        />
                        {m.name}
                        {m.guest && (
                          <span className="text-xs text-muted-foreground">
                            (guest)
                          </span>
                        )}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Your name</Label>
                  <input
                    id="name"
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="e.g. Keyoni"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Your color</Label>
                  <ColorPicker
                    selected={colorInput}
                    disabledColors={usedColors}
                    onPick={setColorInput}
                  />
                </div>
                <Button onClick={claimMember} disabled={!canPick}>
                  Join the group
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---- Main view --------------------------------------------------------

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        {/* Header + identity + admin control */}
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Board Game Tracker</CardTitle>
            <CardDescription>
              Rate the games your group plays, leave comments, and track who wins.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: me?.color }}
                />
                <span className="font-semibold">{me?.name}</span>
                <span className="text-muted-foreground">(you)</span>
              </div>
              <Button variant="ghost" size="sm" onClick={switchIdentity}>
                Not you? Switch
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Your color</Label>
              <ColorPicker
                selected={me?.color}
                disabledColors={usedColors.filter((c) => c !== me?.color)}
                onPick={changeMyColor}
              />
            </div>

            {/* Admin gate */}
            <div className="border-t pt-4">
              {isAdmin ? (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <LockOpen className="h-4 w-4" /> Admin unlocked — you can add
                    games and record winners.
                  </span>
                  <Button variant="ghost" size="sm" onClick={lockAdmin}>
                    Lock
                  </Button>
                </div>
              ) : showAdminInput ? (
                <div className="space-y-2">
                  <Label htmlFor="admin">Admin password</Label>
                  <div className="flex gap-2">
                    <input
                      id="admin"
                      type="password"
                      value={adminInput}
                      onChange={(e) => {
                        setAdminInput(e.target.value);
                        setAdminError(false);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && unlockAdmin()}
                      placeholder="Password"
                      className={inputClass}
                    />
                    <Button onClick={unlockAdmin}>Unlock</Button>
                  </div>
                  {adminError && (
                    <p className="text-xs text-destructive">
                      Incorrect password.
                    </p>
                  )}
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowAdminInput(true)}
                >
                  <Lock className="h-4 w-4" /> Unlock admin
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Admin-only: add game + log play */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Admin</CardTitle>
              <CardDescription>Add games and record results.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="gameName">Add a game</Label>
                <div className="flex gap-2">
                  <input
                    id="gameName"
                    type="text"
                    value={gameNameInput}
                    onChange={(e) => setGameNameInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addGame()}
                    placeholder="e.g. Wingspan"
                    className={inputClass}
                  />
                  <Button onClick={addGame} className="gap-1 whitespace-nowrap">
                    <Plus className="h-4 w-4" /> Add
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Log a play</Label>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select value={playGameId} onValueChange={setPlayGameId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Game" />
                    </SelectTrigger>
                    <SelectContent>
                      {gameEntries.map(([id, g]) => (
                        <SelectItem key={id} value={id}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input
                    type="date"
                    value={playDate}
                    onChange={(e) => setPlayDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* Competitive vs co-op */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={playMode === "competitive" ? "default" : "outline"}
                    onClick={() => setPlayMode("competitive")}
                    className="gap-1"
                  >
                    <Trophy className="h-4 w-4" /> Competitive
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={playMode === "coop" ? "default" : "outline"}
                    onClick={() => setPlayMode("coop")}
                    className="gap-1"
                  >
                    <Users className="h-4 w-4" /> Co-op
                  </Button>
                </div>

                {/* Co-op outcome */}
                {playMode === "coop" && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={playCoopResult === "won" ? "default" : "outline"}
                      onClick={() => setPlayCoopResult("won")}
                    >
                      Group won
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={playCoopResult === "lost" ? "default" : "outline"}
                      onClick={() => setPlayCoopResult("lost")}
                    >
                      Group lost
                    </Button>
                  </div>
                )}

                {/* Winner(s) / players multi-select */}
                <div className="space-y-2">
                  <Label className="text-xs">
                    {playMode === "coop"
                      ? "Who played (everyone is credited a win if the group won)"
                      : "Winner(s) — tap one, or several for a team/tie"}
                  </Label>
                  {memberEntries.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No members yet — add a guest below.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {memberEntries.map(([id, m]) => {
                        const selected = (playMode === "coop"
                          ? playPlayers
                          : playWinnerIds
                        ).includes(id);
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => togglePlayer(id)}
                            className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                              selected
                                ? "border-foreground bg-accent"
                                : "border-border hover:bg-accent"
                            }`}
                          >
                            <span
                              className="h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: m.color }}
                            />
                            {m.name}
                            {m.guest ? " (guest)" : ""}
                            {selected && <Check className="h-3.5 w-3.5" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Quick-add a one-off guest (becomes a claimable member) */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={guestNameInput}
                    onChange={(e) => setGuestNameInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addGuest()}
                    placeholder="Add a guest by name"
                    className={inputClass}
                  />
                  <Button
                    variant="outline"
                    onClick={addGuest}
                    disabled={!guestNameInput.trim()}
                    className="gap-1 whitespace-nowrap"
                  >
                    <Plus className="h-4 w-4" /> Guest
                  </Button>
                </div>

                {/* Optional places & scores */}
                <div className="space-y-2 rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Places &amp; scores (optional)</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={addResultRow}
                      className="gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add player
                    </Button>
                  </div>
                  {playResults.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      Skip this for games without scores or rankings.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {playResults.map((row, i) => (
                        <div
                          key={i}
                          className="space-y-2 rounded-md border border-dashed p-2"
                        >
                          {/* Player on its own line so the dropdown has room */}
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <Select
                                value={row.playerId}
                                onValueChange={(v) =>
                                  updateResultRow(i, "playerId", v)
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Player" />
                                </SelectTrigger>
                                <SelectContent>
                                  {memberEntries.map(([id, m]) => (
                                    <SelectItem key={id} value={id}>
                                      {m.name}
                                      {m.guest ? " (guest)" : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeResultRow(i)}
                              aria-label="Remove player"
                              className="shrink-0 text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          {/* Place + score share the next line */}
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={row.place}
                              onChange={(e) =>
                                updateResultRow(i, "place", e.target.value)
                              }
                              placeholder="Place"
                              className={numInputClass}
                            />
                            <input
                              type="number"
                              value={row.score}
                              onChange={(e) =>
                                updateResultRow(i, "score", e.target.value)
                              }
                              placeholder="Score"
                              className={numInputClass}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  onClick={addPlay}
                  disabled={
                    !playGameId ||
                    !playDate ||
                    (playMode === "coop"
                      ? playPlayers.length === 0
                      : playWinnerIds.length === 0)
                  }
                >
                  Record result
                </Button>
                {gameEntries.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Add a game first to log a play.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin-only: manage members (promote guests, delete) */}
        {isAdmin && memberEntries.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Members</CardTitle>
              <CardDescription>
                Promote a guest to a full member, or remove someone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {memberEntries.map(([id, m]) => (
                  <li
                    key={id}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: m.color }}
                      />
                      <span>{m.name}</span>
                      {m.guest && (
                        <span className="text-xs text-muted-foreground">
                          (guest)
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      {m.guest && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => promoteGuest(id)}
                        >
                          Promote
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteMember(id)}
                        aria-label="Delete member"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Wins leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Wins leaderboard</CardTitle>
            <CardDescription>
              Total wins across all plays — team wins and co-op wins count for
              everyone credited.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members yet.</p>
            ) : (
              <ul className="space-y-2">
                {leaderboard.map((m, i) => (
                  <li
                    key={m.id}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-5 text-muted-foreground">{i + 1}.</span>
                      <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: m.color }}
                      />
                      <span className={m.id === myId ? "font-semibold" : ""}>
                        {m.name}
                      </span>
                      {m.guest && (
                        <span className="text-xs text-muted-foreground">
                          (guest)
                        </span>
                      )}
                      {i === 0 && m.wins > 0 && (
                        <Trophy className="h-4 w-4 fill-amber-400 text-amber-400" />
                      )}
                    </span>
                    <span className="font-medium">
                      {m.wins} {m.wins === 1 ? "win" : "wins"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Games list */}
        {gameEntries.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No games yet.
              {isAdmin
                ? " Add one above to get started."
                : " Ask Keyoni to add some."}
            </CardContent>
          </Card>
        ) : (
          gameEntries.map(([gameId, game]) => {
            const ratings = game.ratings || {};
            const ratingEntries = Object.entries(ratings).filter(
              ([, r]) => r && r.stars
            );
            const avg =
              ratingEntries.length > 0
                ? ratingEntries.reduce((s, [, r]) => s + r.stars, 0) /
                  ratingEntries.length
                : 0;
            const gamePlays = playsByGame[gameId] || [];
            return (
              <Card key={gameId}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <CardTitle className="text-xl">{game.name}</CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        {ratingEntries.length > 0 ? (
                          <>
                            <Stars value={Math.round(avg)} />
                            <span>
                              {avg.toFixed(1)} · {ratingEntries.length}{" "}
                              {ratingEntries.length === 1 ? "rating" : "ratings"}
                            </span>
                          </>
                        ) : (
                          <span>No ratings yet</span>
                        )}
                        <span>· {gamePlays.length} played</span>
                      </CardDescription>
                    </div>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteGame(gameId)}
                        aria-label="Delete game"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* My rating editor */}
                  <MyRatingEditor
                    existing={ratings[myId]}
                    onSave={(r) => saveMyRating(gameId, r)}
                  />

                  {/* Everyone's ratings + comments */}
                  {ratingEntries.length > 0 && (
                    <div className="space-y-2">
                      {ratingEntries
                        .filter(([mid]) => mid !== myId)
                        .map(([mid, r]) => {
                          const author = members[mid];
                          return (
                            <div key={mid} className="text-sm">
                              <div className="flex items-center gap-2">
                                <span
                                  className="h-2.5 w-2.5 rounded-full"
                                  style={{ backgroundColor: author?.color }}
                                />
                                <span className="font-medium">
                                  {author?.name || "Someone"}
                                </span>
                                <Stars value={r.stars} />
                              </div>
                              {r.comment && (
                                <p className="ml-[18px] text-muted-foreground">
                                  {r.comment}
                                </p>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {/* Play history */}
                  {gamePlays.length > 0 && (
                    <div className="space-y-1 border-t pt-3">
                      <Label className="text-xs">Play history</Label>
                      <ul className="space-y-1 text-sm">
                        {gamePlays.map((p) => {
                          const isCoop = p.mode === "coop";
                          const coopWon = p.coopResult === "won";
                          const winners = winnersOf(p);
                          // Optional per-player results, ranked by place then score.
                          const resultRows = Object.entries(p.results || {})
                            .map(([pid, r]) => ({ pid, ...r }))
                            .sort(
                              (a, b) =>
                                (a.place ?? Infinity) - (b.place ?? Infinity) ||
                                (b.score ?? -Infinity) - (a.score ?? -Infinity)
                            );
                          return (
                            <li key={p.pid} className="space-y-1">
                              <div className="flex items-start justify-between gap-2">
                                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                  {isCoop ? (
                                    <>
                                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                      <span>{formatDate(p.date)}</span>
                                      <span className="text-muted-foreground">
                                        ·
                                      </span>
                                      <span
                                        className={`font-medium ${
                                          coopWon
                                            ? "text-emerald-500"
                                            : "text-muted-foreground"
                                        }`}
                                      >
                                        Co-op {coopWon ? "win" : "loss"}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <Trophy className="h-3.5 w-3.5 text-amber-400" />
                                      <span>{formatDate(p.date)}</span>
                                      <span className="text-muted-foreground">
                                        ·
                                      </span>
                                      {winners.length === 0 ? (
                                        <span className="text-muted-foreground">
                                          No winner
                                        </span>
                                      ) : (
                                        winners.map((wid) => (
                                          <span
                                            key={wid}
                                            className="inline-flex items-center gap-1"
                                          >
                                            <span
                                              className="h-2.5 w-2.5 rounded-full"
                                              style={{
                                                backgroundColor:
                                                  members[wid]?.color ||
                                                  GUEST_COLOR,
                                              }}
                                            />
                                            <span>
                                              {members[wid]?.name || "Unknown"}
                                            </span>
                                          </span>
                                        ))
                                      )}
                                    </>
                                  )}
                                </span>
                                {isAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => deletePlay(p.pid)}
                                    aria-label="Delete play"
                                    className="text-muted-foreground hover:text-destructive"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                              {isCoop && (p.players || []).length > 0 && (
                                <div className="ml-[18px] flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                                  {(p.players || []).map((pid) => (
                                    <span
                                      key={pid}
                                      className="inline-flex items-center gap-1"
                                    >
                                      <span
                                        className="h-2 w-2 rounded-full"
                                        style={{
                                          backgroundColor:
                                            members[pid]?.color || GUEST_COLOR,
                                        }}
                                      />
                                      {members[pid]?.name || "Unknown"}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {resultRows.length > 0 && (
                                <ul className="ml-[18px] space-y-0.5 text-xs text-muted-foreground">
                                  {resultRows.map((r) => (
                                    <li
                                      key={r.pid}
                                      className="flex items-center gap-1.5"
                                    >
                                      {r.place != null && (
                                        <span className="font-medium">
                                          {r.place}.
                                        </span>
                                      )}
                                      <span
                                        className="h-2 w-2 rounded-full"
                                        style={{
                                          backgroundColor:
                                            members[r.pid]?.color || GUEST_COLOR,
                                        }}
                                      />
                                      <span>
                                        {members[r.pid]?.name || "Unknown"}
                                      </span>
                                      {r.score != null && (
                                        <span>— {r.score} pts</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BoardGameTracker;
